import shippingOptions from "../../../server/shipping-policy.json";

export const BUSINESS = {
  brandName: "Hooligans Eyewear",
  legalName: "Hooligans Eyewear Inc.",
  url: "https://hooliganseyewear.com/",
  email: "support@hooliganseyewear.com",
  phone: "+13473203876",
  phoneDisplay: "+1 (347) 320-3876",
  address: {
    streetAddress: "25-84 Steinway St",
    addressLocality: "Astoria",
    addressRegion: "NY",
    postalCode: "11103",
    addressCountry: "US",
  },
  addressDisplay: "25-84 Steinway St, Astoria, NY 11103, United States",
  supportHours: "Monday–Friday, 9:00 AM–5:00 PM ET",
  sameAs: [
    "https://www.instagram.com/hooliganseyewear/",
    "https://www.tiktok.com/@hooliganseyewear",
    "https://www.facebook.com/profile.php?id=100090258177839",
    "https://www.pinterest.com/DesignerEyewearBoutique/",
  ],
} as const;

/**
 * Exact HTTPS sources allowed in Product JSON-LD.
 *
 * Product media is served both through the storefront image proxy and from the
 * catalog CDN. Keep the CDN path scoped to OpenCart's catalog tree so adding a
 * trusted host does not admit unrelated files from that host.
 */
export const PRODUCT_IMAGE_SOURCES = [
  { origin: BUSINESS.url, pathPrefix: "/merchant-images/" },
  { origin: BUSINESS.url, pathPrefix: "/image/catalog/" },
  {
    origin: "https://cdn.hooliganseyewear.com/",
    pathPrefix: "/image/catalog/",
  },
] as const;

/**
 * Shipping and return terms published in Product/Offer JSON-LD.
 *
 * Google cross-checks these against the visible policy pages, and a mismatch
 * suppresses the merchant listing enhancements. Keep this in sync with
 * /delivery and /refund_policy — it is the single source of truth for the
 * structured-data copy of those terms.
 */
const RETURN_DAYS = 14;
const RETURN_SHIPPING_FEE = "9.95";

export const OFFER_TERMS = {
  currency: "USD",
  country: "US",
  shippingRate: "0",
  handlingDays: { min: 0, max: 1 },
  transitDays: { min: 1, max: 5 },
  returnDays: RETURN_DAYS,
  returnShippingFee: RETURN_SHIPPING_FEE,
  shippingTitle: "Free U.S. standard shipping · International $20",
  handlingDescription: "Ships same or next business day · Mon–Fri",
  shippingOptions,
  // PDP-only presentation facts. These remain out of Offer JSON-LD.
  visiblePolicy: {
    shipping: {
      featureSubtitle: "Free U.S. standard shipping · International $20",
      carrierLabel: "Shipped with",
      carriers: ["USPS", "UPS", "FedEx"],
      tracking: [
        {
          title: "Order placed",
          description: "Payment confirmed — your order enters our queue.",
        },
        {
          title: "Shipped",
          description: "We email your tracking number the moment it ships.",
        },
        {
          title: "Track",
          description: "Follow your package on the carrier's site.",
        },
        {
          title: "Delivered",
          description: "Your eyewear arrives at your door. Enjoy.",
        },
      ],
      customs:
        "International shipping is $20. Customs duties and taxes are paid by the buyer.",
      addressChanges:
        "Double-check your address: orders can't be changed or canceled once shipped — email us right after ordering.",
    },
    returns: {
      finalSale: {
        title: "Final Sale — returns not accepted",
        description:
          "This item is not eligible for a return, exchange or refund.",
        reminder:
          "Please review the frame details and measurements before ordering. Final Sale purchases are non-returnable.",
        featureTitle: "Final Sale",
        featureSubtitle: "returns not accepted",
        headerTooltip:
          "This item cannot be returned or exchanged. Promo codes do not apply.",
        faq: "This item is final sale and not returnable.",
      },
      standard: {
        title: "14-day easy returns",
        description:
          "Not in love with it? Send it back within 14 days of delivery.",
        featureTitle: "Easy US Returns",
        // The fee is shown next to Add to cart, not only in the accordion.
        featureSubtitle: `${RETURN_DAYS}-day returns · $${RETURN_SHIPPING_FEE} return shipping`,
        process: [
          {
            title: "Email us",
            description:
              "Message support with your order number and reason for return.",
          },
          {
            title: "Get your label",
            description: "Once approved, we email you a prepaid return label.",
          },
          {
            title: "Ship it back",
            description:
              "Pack it with the case, cloth & tags, then drop it off.",
          },
          {
            title: "Get refunded",
            description:
              "After inspection, your refund is issued in 1–5 business days.",
          },
        ],
        condition:
          "Must be unworn and complete — case, cloth and the original tag still attached.",
        fee: "A $9.95 return shipping fee applies. Final-sale and international orders aren't eligible.",
        faq: "No worries — we offer easy returns. If you're not satisfied, you can return your order within our return window for a refund.",
      },
      unknown: {
        title: "Return eligibility is confirmed at checkout",
        featureTitle: "Returns",
        featureSubtitle: "Return eligibility is confirmed at checkout",
        faq: "Return eligibility is confirmed at checkout.",
      },
    },
  },
} as const;

/** Verified store-wide inventory condition for every catalog product. */
export const PRODUCT_ITEM_CONDITION = "https://schema.org/NewCondition";

export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BUSINESS.url}#organization`,
  name: BUSINESS.brandName,
  legalName: BUSINESS.legalName,
  url: BUSINESS.url,
  logo: `${BUSINESS.url}logo.svg`,
  description:
    "Authentic designer sunglasses and eyeglasses for less, featuring genuine frames from top brands.",
  email: BUSINESS.email,
  telephone: BUSINESS.phone,
  address: {
    "@type": "PostalAddress",
    ...BUSINESS.address,
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: BUSINESS.email,
    telephone: BUSINESS.phone,
    availableLanguage: ["English"],
    areaServed: "Worldwide",
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "https://schema.org/Monday",
        "https://schema.org/Tuesday",
        "https://schema.org/Wednesday",
        "https://schema.org/Thursday",
        "https://schema.org/Friday",
      ],
      opens: "09:00",
      closes: "17:00",
    },
  },
  sameAs: BUSINESS.sameAs,
};
